import * as Api from "@api";
import * as C from "@components";
import clsx from "clsx";
import css from "./battery-voltage.module.scss";
import Accordion from "@/components/Accordion/Accordion";
import SuggestedSproutSettingsSection from "@/parts/TipsTricksSection/SuggestedSproutSettingsSection";
import {ContentItem} from "@api";

export const BatteryVoltageCardContentKeys = [
    "battery-voltage.headline",
    "battery-voltage.body",
    "battery-voltage.low.headline",
    "battery-voltage.low.body",
    "battery-voltage.medium.headline",
    "battery-voltage.medium.body",
    "battery-voltage.high.headline",
    "battery-voltage.high.body",
] as const;

type VoltageLevel = "low" | "medium" | "high";
type PageContentKeys = `battery-voltage.${VoltageLevel}.${"headline" | "body"}`;

interface BatteryVoltageCardProps {
    imageDirectory: string;
    pageContent: Api.Content<typeof BatteryVoltageCardContentKeys[number]>;
    sproutTipsTricksContent?: Record<string, ContentItem>;
    imagePrefix?: string;
    imageExtension?: string;
    customOrder?: VoltageLevel[];
}

export const BatteryVoltageCard = ({ imageDirectory, pageContent, sproutTipsTricksContent, imagePrefix, imageExtension = 'png', customOrder }: BatteryVoltageCardProps): React.FunctionComponentElement<BatteryVoltageCardProps> => {
    const defaultOrder: VoltageLevel[] = ["low", "medium", "high"];
    customOrder = customOrder || defaultOrder;

    return (
        <C.Card className={css["card"]} layout={{ all: { paddings: { all: C.Padding.D24 }, variant: C.CardVariant.CenteredContent, width: C.Width.W100P }, ipad: { paddings: { y: C.Padding.D86 } } }} useWrapper>
            <div>
                <C.SideBySideText
                    className={css['top_section_text']}
                    keys={["battery-voltage.headline", "battery-voltage.body"]}
                    items={{
                        "battery-voltage.headline": { typography: C.Typography.H3, maxWidth: C.Width.W448 },
                        "battery-voltage.body": { typography: C.Typography.P2, maxWidth: C.Width.W454 }
                    }}
                    layout={{ all: { margins: { bottom: C.Margin.D60 } } }}
                    pageContent={pageContent}
                />
                <C.Container horizontalGap={70} className={css['bottom-container']}>
                    {customOrder.map(level => {
                        const keyHeadline: PageContentKeys = `battery-voltage.${level}.headline`;
                        const keyBody: PageContentKeys = `battery-voltage.${level}.body`;

                        return (
                            <C.CaptionedImage
                                key={level}
                                imageProps={{
                                    src: `${imageDirectory}${imagePrefix ?? "battery-voltage"}.${level}.${imageExtension}`,
                                    width: 260,
                                    height: 200,
                                    alt: level.charAt(0).toUpperCase() + level.slice(1)
                                }}
                                leftAligned
                            >
                                <div className={clsx(C.maxWidthClass(C.Width.W260), C.marginsClass({ top: C.Margin.D10 }), css['bottom_container_item_labels'])}>
                                    <C.PageContent {...pageContent[keyHeadline]} typography={C.Typography.H7} className={css['item-title']} />
                                    <C.PageContent {...pageContent[keyBody]} typography={C.Typography.P2} />
                                </div>
                            </C.CaptionedImage>
                        );
                    })}
                </C.Container>

                {
                    sproutTipsTricksContent
                        ? <Accordion
                            labelOpen='Collapse'
                            labelClose='Expand for suggested settings'
                            className='mt-10 font-semibold'
                        >
                            <SuggestedSproutSettingsSection pageContent={sproutTipsTricksContent} />
                        </Accordion>
                        : null
                }
            </div>
        </C.Card>
    );
};

// TODO: Implement key interpolation.
